/**
 * Recognising the tab that comes BACK from an external flow.
 *
 * DocuSeal signing opens in its own tab, and the refresh token lives in
 * per-tab sessionStorage — so when the provider redirects that tab to this
 * origin, it has no session and would render the sign-in screen. That reads as
 * "signing signed me out". This path renders a sessionless "done — close this
 * tab" screen instead; the ORIGINAL tab owns the state and moves on by itself
 * (it polls, and the webhook flips the answer server-side).
 *
 * ONE path now. The two billing ones went with Stripe Checkout (spec
 * 2026-09-07 §10): payment happens in the apps, so no tab returns from a
 * payment.
 *
 * THIS PATH IS NO LONGER THE returnUrl, and it is kept deliberately. The api
 * now serves the landing page itself at /public/consent/complete
 * (src/consent/pages.ts), because /consent/complete only ever resolved while
 * Caddy sat in front splitting page paths from api paths — on the ALB, which
 * has a single default action into the api, it 404'd on a real signer. What
 * still arrives here is a submission created BEFORE that change: DocuSeal
 * holds the returnUrl it was given, so signatures in flight across the deploy
 * come back to the old path for one CONSENT_LINK_TTL window. Caddy serves this
 * entry for them. Delete it once that window has passed.
 *
 * A path added here in future must still be one Caddy's try_files serves,
 * i.e. NOT under /v1, /public, /webhooks, /healthz or /readyz — those are
 * proxied to the api and never reach the SPA.
 */

export type ExternalReturn = 'consent-complete';

const RETURN_PATHS: Record<string, ExternalReturn> = {
  '/consent/complete': 'consent-complete',
};

export function externalReturn(pathname: string): ExternalReturn | null {
  return RETURN_PATHS[pathname.replace(/\/+$/, '') || '/'] ?? null;
}

export const RETURN_COPY: Record<ExternalReturn, { title: string; detail: string; failed?: boolean }> = {
  'consent-complete': {
    title: 'Signature received',
    detail:
      'You can close this tab. Your ImageShield tab confirms the consent on its own within a few seconds.',
  },
};
